import React from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import { BilldaLogo } from '@billda/shared-ui';

interface BrandSectionProps {
  /**
   * Variant determines the tagline text
   * @default 'login'
   */
  variant?: 'login' | 'invite';
  /**
   * Organization name to display (for invite variant)
   */
  organizationName?: string;
}

/**
 * Molecule: Billda logo + tagline text combination
 * Centered flex column for branded side
 * Always uses white logo on gradient background
 */
export const BrandSection: React.FC<BrandSectionProps> = ({
  variant = 'login',
  organizationName,
}) => {
  const theme = useTheme();
  // Always use white logo on dark background
  const logoVariant = 'secondary';
  // Amber accent for badge — high contrast on dark background
  const amber = theme.palette.primary.main;

  if (variant === 'invite') {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 2,
          zIndex: 2,
          px: 4,
          textAlign: 'center',
        }}
      >
        <BilldaLogo variant={logoVariant} width={240} />

        {organizationName && (
          <Typography
            variant="h3"
            component="p"
            sx={{
              color: 'white',
              fontWeight: 600,
              maxWidth: '500px',
            }}
          >
            {organizationName}
          </Typography>
        )}

        <Typography
          variant="h5"
          component="p"
          sx={{
            color: 'rgba(255,255,255,0.85)',
            fontWeight: 500,
            maxWidth: '500px',
          }}
        >
          Welcome to your team!
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 3,
        zIndex: 2,
        px: 6,
        textAlign: 'center',
        maxWidth: 520,
      }}
    >
      <BilldaLogo variant={logoVariant} width={200} />

      {/* Badge — amber on dark for strong contrast */}
      <Box
        sx={{
          display: 'inline-block',
          px: 2,
          py: 0.75,
          borderRadius: '999px',
          border: `1px solid ${amber}50`,
          bgcolor: `${amber}18`,
        }}
      >
        <Typography
          variant="caption"
          sx={{
            color: amber,
            fontWeight: 700,
            letterSpacing: '0.07em',
            textTransform: 'uppercase',
            fontSize: '0.7rem',
          }}
        >
          Managing sub-contractors for UK housebuilders
        </Typography>
      </Box>

      {/* Headline */}
      <Box sx={{ mt: -0.5 }}>
        <Typography
          variant="h3"
          component="p"
          sx={{
            color: 'white',
            fontWeight: 700,
            lineHeight: 1.15,
          }}
        >
          One platform for your whole operation.
        </Typography>
        <Typography
          variant="h3"
          component="p"
          sx={{
            color: amber,
            fontWeight: 700,
            lineHeight: 1.15,
            mt: 0.5,
          }}
        >
          From site to payday.
        </Typography>
      </Box>

      {/* Subline */}
      <Typography
        variant="body1"
        component="p"
        sx={{
          color: 'rgba(255,255,255,0.6)',
          lineHeight: 1.65,
        }}
      >
        Billda gives professional sub-contractors the tools to manage price
        work, gangs, day work, and weekly pay. No spreadsheets, no WhatsApp
        chains, no Sunday evening chaos.
      </Typography>
    </Box>
  );
};
